/**
 * TutorNavigator — l'entrée des ports pour l'espace tuteur sur le design system.
 *
 * Monté par la bascule de `common/main-menu/BottomTabsNavigator.js` (par `TutorShellBridge.js`, qui lui
 * descend ce que les ports ne peuvent pas importer) : un tuteur, hors live, dont l'interrupteur
 * `tutor_ds` est relu allumé pour ce compte. `BottomTabsNavigator` garde ce qui n'est pas de l'écran —
 * les relectures du compte et de `static-data`, les notifications, la garde de mise à jour.
 *
 * Ce que ce fichier possède : le THÈME de l'espace (Clair par défaut, `useTutorThemeScope`), les
 * onglets du masteur sous la barre du DS (`TutorTabBar`), chacun dans son fond et sous l'en-tête de
 * l'espace (`TutorTabLayout`), et la barre d'état au thème.
 *
 * Les onglets sont ceux que la coquille legacy calcule (`tutorTabScreens.js`, filtrés par ses droits :
 * Mes élèves avec `my_students`, Offres de cours avec un droit de mission, Ressources avec
 * `tutor_resources`), sous les mêmes noms de route. Chacun rend l'écran que son lot a inscrit
 * (`tutorSpaceTabs` : l'accueil, « Ma journée »), sinon son écran legacy, dans la coquille. Écoles,
 * Mes classes et Revue d'activités ne sont pas portés : un tuteur qui en a le droit migre comme les
 * autres (Wajih, 02/11).
 *
 * **Le candidat** (`tutor-candidate`, même aussi `tutor`) a sa candidature à la place de l'accueil
 * (`tutorCandidateTabs`), qui porte son propre en-tête (la marque et la pastille de compte) : ni
 * l'en-tête de l'espace au-dessus, ni de barre d'onglets pour un seul onglet (arbitrage 1 de l'audit
 * de la candidature). Ses autres onglets, s'il en a les droits, gardent leur écran legacy.
 *
 * Un onglet porté dit lui-même ses échecs réseau tant qu'il a le focus (`useOwnNetworkFailuresWhileFocused`) :
 * les boîtes de l'ancien kit se taisent. Un onglet resté legacy (ceux du candidat) garde les siennes.
 */
import React from "react"
import { createBottomTabNavigator, type BottomTabBarProps } from "@react-navigation/bottom-tabs"
import { useSelector } from "react-redux"

import { ScreenBackground } from "../../../../../design-system"
import { isTutorCandidate } from "../../../../../featureFlags/tutorDsFF"
import { selectAccount } from "../../../view-models/shared/displayedStudent"

import { useOwnNetworkFailuresWhileFocused } from "../../hooks/useOwnNetworkFailuresWhileFocused"
import { useTutorThemeScope } from "../../hooks/useTutorThemeScope"
import { ParentStatusBar } from "../parent/ParentStatusBar"
import { routeNameToTutorTabKey, TUTOR_TAB_KEY_TO_ROUTE, TUTOR_TAB_TITLES, type TutorTabKey } from "./tutorRoutes"
import { TutorSupportContext } from "./TutorShellHeader"
import { tutorCandidateTabs, tutorSpaceTabs } from "./tutorSpaceRoutes"
import { type TutorSpaceTabName } from "./tutorSpaceRouteTypes"
import { TutorTabBar } from "./TutorTabBar"
import { TutorTabLayout } from "./TutorTabLayout"

const Tab = createBottomTabNavigator()

/** Un onglet de la coquille legacy : son nom de route, son écran, sa pastille (`tabBarBadge`). */
export type TutorLegacyTab = {
    name: string
    // L'écran legacy, monté tel quel dans la coquille tant que son lot n'a rien inscrit.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: React.ComponentType<any>
    badge?: number | null
}

export type TutorNavigatorProps = {
    tabs: TutorLegacyTab[]
    /** Ouvre la messagerie du support. Absent : pas de bouton d'aide. */
    onContactSupport?: () => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TabScreen = React.ComponentType<any>

/** Un composant d'onglet par écran, gardé d'un rendu à l'autre : reconstruit, l'onglet serait remonté. */
const wrappedTabs = new WeakMap<TabScreen, TabScreen>()

/**
 * L'écran d'un onglet, entouré de son fond et de l'en-tête (la lueur sur l'accueil). `navigation` et
 * `route` de l'onglet sont relayés : un écran legacy les lit dans ses props. Un écran porté
 * (`ported`) tient ses échecs réseau le temps de son focus.
 */
function tabComponent(Screen: TabScreen, ported: boolean): TabScreen {
    const cached = wrappedTabs.get(Screen)
    if (cached) return cached
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Wrapped: TabScreen = (props: any) => {
        useOwnNetworkFailuresWhileFocused(ported)
        return (
            <TutorTabLayout home={props.route?.name === TUTOR_TAB_KEY_TO_ROUTE.dashboard}>
                <Screen {...props} />
            </TutorTabLayout>
        )
    }
    Wrapped.displayName = `TutorTab(${Screen.displayName ?? Screen.name ?? "écran"})`
    wrappedTabs.set(Screen, Wrapped)
    return Wrapped
}

/** Un écran d'onglet qui porte son propre en-tête (la candidature) : son fond seul, pas de lueur. */
const bareTabs = new WeakMap<TabScreen, TabScreen>()

function bareTabComponent(Screen: TabScreen): TabScreen {
    const cached = bareTabs.get(Screen)
    if (cached) return cached
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Wrapped = (props: any) => (
        <ScreenBackground>
            <Screen {...props} />
        </ScreenBackground>
    )
    Wrapped.displayName = `TutorBareTab(${Screen.displayName ?? Screen.name ?? "écran"})`
    bareTabs.set(Screen, Wrapped)
    return Wrapped
}

export function TutorNavigator(props: TutorNavigatorProps) {
    const { tabs, onContactSupport } = props
    useTutorThemeScope()
    const candidate = useSelector((state: Parameters<typeof selectAccount>[0]) =>
        isTutorCandidate(selectAccount(state)?.roles)
    )

    const screens = tabs.flatMap((tab) => {
        const key = routeNameToTutorTabKey(tab.name)
        if (key == null) return []
        // Le candidat : sa candidature à la place de l'accueil, le reste en legacy.
        const application = candidate ? tutorCandidateTabs[tab.name as "dashboard"] : undefined
        if (application != null) {
            return [{ name: tab.name, key, badge: tab.badge, component: bareTabComponent(application) }]
        }
        const ported = candidate ? undefined : tutorSpaceTabs[tab.name as TutorSpaceTabName]
        const component = tabComponent(ported ?? tab.component, ported != null)
        return [{ name: tab.name, key, badge: tab.badge, component }]
    })
    const badges = Object.fromEntries(
        screens.filter((screen) => (screen.badge ?? 0) > 0).map((screen) => [screen.key, screen.badge as number])
    ) as Partial<Record<TutorTabKey, number>>

    return (
        <TutorSupportContext.Provider value={onContactSupport}>
            <Tab.Navigator
                // Sans identifiant : les onglets se rejoignent par leur nom, comme ceux du legacy.
                id={undefined}
                // Le candidat qui n'a que sa candidature : un seul onglet n'est pas une navigation.
                tabBar={(tabBarProps: BottomTabBarProps) =>
                    candidate && screens.length === 1 ? null : <TutorTabBar {...tabBarProps} badges={badges} />
                }
                screenOptions={{
                    headerShown: false,
                    // Chaque onglet peint son fond (`TutorTabLayout`) : la scène ne doit rien poser dessous.
                    sceneStyle: { backgroundColor: "transparent" },
                }}
            >
                {screens.map((screen) => (
                    <Tab.Screen
                        key={screen.name}
                        name={screen.name}
                        component={screen.component}
                        options={{ headerTitle: TUTOR_TAB_TITLES[screen.key] }}
                    />
                ))}
            </Tab.Navigator>
            {/* La barre d'état suit le thème ; montée après la coquille, c'est elle qui gagne. */}
            <ParentStatusBar />
        </TutorSupportContext.Provider>
    )
}
